import { ReactNode } from "react";
import { BlockCard, MetricTile } from "../primitives";
import { BlockBaseProps, Delta } from "../types";
import RingLegend from "./RingLegend";
import SchoolChips, { SchoolRef } from "./SchoolChips";
import HeadlineFiguresBlockSkeleton from "./Skeleton";
import StatusSplitTile, { StatusBand } from "./StatusSplitTile";
import styles from "./style.module.scss";

export interface HeadlineFigure {
  key: string;
  label: string;
  value: string | number;
  unit?: string;
  icon?: ReactNode;
  delta?: Delta;
  definition?: string;
  /** Draws the progress ring with its legend. */
  ring?: { ratio: number; legendA: string; legendB: string };
  /** Draws red school chips under the figure. */
  alertSchools?: SchoolRef[];
}

interface HeadlineFiguresBlockProps extends BlockBaseProps {
  bands: StatusBand[];
  figures: HeadlineFigure[];
  title?: string;
  openBand?: string | null;
  onToggleBand?: (key: string) => void;
  onSelectSchool?: (id: number | string) => void;
  bandDefinition?: string;
  /** Shows the dashed definition footer on every tile. */
  showDefinitions?: boolean;
}

/** The four headline tiles: status split, regular readers, gaps and level-ups. */
function HeadlineFiguresBlock({
  bands,
  figures,
  title = "The headline figures",
  openBand,
  onToggleBand,
  onSelectSchool,
  bandDefinition,
  showDefinitions,
  loading,
  empty,
  className,
}: HeadlineFiguresBlockProps) {
  if (loading) {
    return (
      <HeadlineFiguresBlockSkeleton
        count={figures.length || 3}
        withDefinitions={showDefinitions}
        className={className}
      />
    );
  }

  return (
    <BlockCard title={title} compactTitle empty={empty} className={className}>
      <div className={styles.grid}>
        <StatusSplitTile
          bands={bands}
          openBand={openBand}
          onToggleBand={onToggleBand}
          onSelectSchool={onSelectSchool}
          definition={showDefinitions ? bandDefinition : undefined}
        />

        {figures.map((figure) => (
          <MetricTile
            key={figure.key}
            emphasis
            label={figure.label}
            icon={figure.icon}
            value={figure.value}
            unit={figure.unit}
            delta={figure.delta}
            definition={showDefinitions ? figure.definition : undefined}
            footer={
              figure.alertSchools && (
                <SchoolChips
                  schools={figure.alertSchools}
                  onSelect={onSelectSchool}
                  tone="alert"
                />
              )
            }
          >
            {figure.ring && (
              <RingLegend
                ratio={figure.ring.ratio}
                legendA={figure.ring.legendA}
                legendB={figure.ring.legendB}
              />
            )}
          </MetricTile>
        ))}
      </div>
    </BlockCard>
  );
}

export default HeadlineFiguresBlock;
